import type {
  GetInvoicesRequest,
  GetInvoicesResult,
  InvoiceSortParam,
} from '@features/invoices/api/get-invoices/getInvoices.types';
import type { BalanceStatus } from '@features/invoices/api/types/balance-status.types';
import type { UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '@contexts/auth-provider/hooks/use-auth/useAuth';
import { useOrganizations } from '@contexts/organizations-provider';
import { getInvoices } from '@features/invoices/api/get-invoices/getInvoices';
import { useQuery } from '@tanstack/react-query';

export interface UseInvoicesParams {
  sort?: InvoiceSortParam;
  pageSize: number;
  pageIndex: number;
  searchText?: string;
  dueDateStart?: string | null;
  dueDateEnd?: string | null;
  issuedDateStart?: string | null;
  issuedDateEnd?: string | null;
  balanceStatuses?: BalanceStatus[];
  currencies?: string[];
  platforms?: string[];
  locations?: string[];
}

export function useInvoices(
  params: UseInvoicesParams
): UseQueryResult<GetInvoicesResult, Error> {
  const { isAuthenticated } = useAuth();
  const { selectedOrganization } = useOrganizations();

  const apiParams: GetInvoicesRequest = {
    organizationId: selectedOrganization?.id ?? '',
    sort: params.sort,
    top: params.pageSize,
    skip: params.pageIndex * params.pageSize,
    ...(params.searchText && { searchText: params.searchText }),
    ...(params.dueDateStart && { dueDateStart: params.dueDateStart }),
    ...(params.dueDateEnd && { dueDateEnd: params.dueDateEnd }),
    ...(params.issuedDateStart && { issuedOnStartDate: params.issuedDateStart }),
    ...(params.issuedDateEnd && { issuedOnEndDate: params.issuedDateEnd }),
    ...(params.balanceStatuses?.length && { balanceStatus: params.balanceStatuses }),
    ...(params.currencies?.length && { currency: params.currencies }),
    ...(params.platforms?.length && { platform: params.platforms }),
    ...(params.locations?.length && { location: params.locations }),
  };

  return useQuery<GetInvoicesResult, Error>({
    queryKey: [
      'invoices',
      apiParams.organizationId,
      {
        pagination: { pageIndex: params.pageIndex, pageSize: params.pageSize },
        sorting: { sort: params.sort },
        filters: {
          searchText: params.searchText ?? '',
          dueDateStart: params.dueDateStart ?? null,
          dueDateEnd: params.dueDateEnd ?? null,
          issuedDateStart: params.issuedDateStart ?? null,
          issuedDateEnd: params.issuedDateEnd ?? null,
          balanceStatuses: params.balanceStatuses ?? [],
          currencies: params.currencies ?? [],
          platforms: params.platforms ?? [],
          locations: params.locations ?? [],
        },
      },
    ],
    queryFn: () => getInvoices(apiParams),
    enabled: isAuthenticated && !!selectedOrganization,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
